import { EditorState, Modifier, SelectionState } from "draft-js";

import trimEnd from "lodash/fp/trimEnd";
import trimStart from "lodash/fp/trimStart";

/**
 * @param   {EditorState} editorState
 * @returns {EditorState}
 */
export default function trim(editorState) {
  return moveSelectionToEnd(trimContent(trimRight(trimLeft(editorState))));
}

/**
 * @param   {EditorState} editorState
 * @returns {EditorState}
 */
function trimContent(editorState) {
  const contentState = editorState.getCurrentContent();

  const firstBlock = trimBlock(contentState, contentState.getFirstBlock());
  const lastBlock = trimBlock(firstBlock, contentState.getLastBlock());

  return EditorState.push(editorState, lastBlock);
}

/**
 * @param   {ContentState} contentState
 * @param   {ContentBlock} block
 * @returns {ContentState}
 */
function trimBlock(contentState, block) {
  return Modifier.replaceText(
    contentState,
    new SelectionState({
      anchorKey: block.getKey(),
      anchorOffset: 0,
      focusKey: block.getKey(),
      focusOffset: block.getLength(),
    }),
    trimStart(trimEnd(block.getText())),
  );
}

/**
 * @param   {EditorState} editorState
 * @returns {EditorState}
 */
function trimLeft(editorState) {
  const contentState = editorState.getCurrentContent();

  const emptyBlocks = contentState.getBlockMap().takeUntil((currentBlock) => {
    return currentBlock.getText() !== "" && currentBlock.getType() !== "atomic";
  });

  if (emptyBlocks.count() > 0) {
    const lastEmptyBlock = emptyBlocks.last();
    const blockAfter = contentState.getBlockAfter(lastEmptyBlock.getKey());

    if (!blockAfter) {
      return editorState;
    }

    const afterCut = cutSelection(
      editorState,
      editorState.getSelection().merge({
        anchorKey: emptyBlocks.first().getKey(),
        anchorOffset: 0,
        focusKey: emptyBlocks.last().getKey(),
        focusOffset: 0,
      }),
    );

    return EditorState.push(
      editorState,
      afterCut.getCurrentContent().merge({
        blockMap: afterCut.getCurrentContent().getBlockMap().remove(emptyBlocks.first().getKey()),
      }),
      "remove-range",
    );
  }

  return editorState;
}

/**
 * @param   {EditorState} editorState
 * @returns {EditorState}
 */
function trimRight(editorState) {
  const contentState = editorState.getCurrentContent();

  const emptyBlocks = contentState
    .getBlockMap()
    .reverse()
    .takeUntil((currentBlock) => {
      const blockBefore = contentState.getBlockBefore(currentBlock.getKey());

      if (blockBefore) {
        return (
          currentBlock.getText() !== "" && currentBlock.getType() !== "atomic" && blockBefore.getType() !== "atomic"
        );
      }

      return currentBlock.getText() !== "" && currentBlock.getType() !== "atomic";
    });

  if (emptyBlocks.count() === 0) {
    return editorState;
  }

  const blockBefore = contentState.getBlockBefore(emptyBlocks.last().getKey());

  if (blockBefore) {
    return cutSelection(
      editorState,
      editorState.getSelection().merge({
        anchorKey: emptyBlocks.last().getKey(),
        anchorOffset: 0,
        focusKey: blockBefore.getKey(),
        focusOffset: 0,
      }),
    );
  }

  return editorState;
}

/**
 * @param   {EditorState}    editorState
 * @param   {SelectionState} removeSelection
 * @returns {EditorState}
 */
function cutSelection(editorState, removeSelection) {
  const withoutSelection = Modifier.removeRange(editorState.getCurrentContent(), removeSelection, "forward");

  return EditorState.push(editorState, withoutSelection, "remove-range");
}

/**
 * @param   {EditorState} editorState
 * @returns {EditorState}
 */
function moveSelectionToEnd(editorState) {
  const content = editorState.getCurrentContent();
  const blockMap = content.getBlockMap();

  const key = blockMap.last().getKey();
  const length = blockMap.last().getLength();

  return EditorState.acceptSelection(
    editorState,
    editorState.getSelection().merge({
      anchorKey: key,
      anchorOffset: length,
      focusKey: key,
      focusOffset: length,
    }),
  );
}
